import SendInvitationButton from '@/components/actions/send-invitation/send-invitation-button';
import WishlistToggleButton from '@/components/creator-discovery/WishlistToggleButton';
import { useWishlistToggle } from '@/hooks/use-wishlist-toggle';
import { Box } from '@chakra-ui/react';
import { MentionPlatform, type Mention } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

interface MentionActionProps {
  mention: Mention;
}

/**
 * Invitation and wishlist are Instagram-only for now: the affiliate invitation
 * flow and the Discovery-backed wishlist do not support TikTok creators yet.
 * Relax these gates once TikTok onboarding is handled.
 */
const isInstagramMention = (mention: Mention) => mention.platform === MentionPlatform.INSTAGRAM;

/**
 * Invite the mentioning creator to an affiliate program, shown under the
 * creator summary in the mention preview. Reuses the shared SendInvitationButton;
 * the mention author id is the backend creator id, so the invitation flow works
 * as-is. Hidden once the creator is already enrolled with the current brand.
 */
export function MentionInviteButton({ mention }: MentionActionProps) {
  const { t } = useTranslation();
  const { author } = mention;

  if (!isInstagramMention(mention) || author.isEnrolled) {
    return null;
  }

  return (
    <Box pt={1}>
      <SendInvitationButton creatorId={author.id} colorPalette="primary" w="full">
        {t('mentions.invite.cta')}
      </SendInvitationButton>
    </Box>
  );
}

/**
 * Wishlist (favorites) toggle for the mentioning creator, shown in the mention
 * preview header. Reuses the shared Discovery wishlist toggle and hook; the
 * wishlist is keyed on `platformUserId`, so the star is hidden when that
 * identifier is missing (partial enrichment / unsupported platform).
 */
export function MentionWishlistButton({ mention }: MentionActionProps) {
  const { author } = mention;
  const canWishlist = isInstagramMention(mention) && Boolean(author.platformUserId);

  const { isInWishlist, toggle, isToggling, isLoading } = useWishlistToggle(
    canWishlist
      ? {
          platformUserId: author.platformUserId,
          platform: mention.platform,
          username: author.username,
          profilePictureUrl: author.profilePictureUrl,
          followersCount: author.followersCount,
        }
      : null
  );

  if (!canWishlist) {
    return null;
  }

  // Keep the star mounted while the wishlist state resolves or a toggle is in
  // flight, surfacing a spinner instead of unmounting. Unmounting on `isLoading`
  // made the button blank out for a frame after each click, because a successful
  // toggle triggers a background refetch that briefly flips `isLoading` back on.
  return (
    <WishlistToggleButton
      isInWishlist={isInWishlist}
      onClick={toggle}
      loading={isToggling || isLoading}
    />
  );
}
